import { Link } from 'react-router-dom';
import { SectionHeading } from '@/components/ui/section-heading';
import { useMemo } from 'react';
import { ArrowRight, Music2, MapPin, Calendar } from 'lucide-react';
import { useFeaturedTours } from '@/hooks/useFeatured';
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { getTourLink } from '@/lib/linkUtils';
import { artistUrl } from '@/lib/entityLinks';
import { format } from 'date-fns';

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// Deterministic-but-rotating shuffle. Uses an hourly bucket so the surface
// reshuffles within a day, but the same user sees a stable order for ~1 hour.
const sessionRotate = <T,>(items: T[]): T[] => {
  if (items.length <= 1) return items;
  // bucket every ~3 hours so the carousel feels alive without thrashing
  const bucket = Math.floor(Date.now() / (1000 * 60 * 60 * 3));
  let seed = bucket;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const SectionHeader = () => (
  <SectionHeading eyebrow="On Our Radar" title="Featured Tours" to="/discover?tab=trending" />
);

const FeaturedToursCarousel = () => {
  const { data: tours, isLoading } = useFeaturedTours();

  // Build the editorial pool: keep ongoing/upcoming tours preferred, then
  // rotate within the qualified set so the surface doesn't repeat for weeks.
  const pool = useMemo(() => {
    if (!tours?.length) return [];
    const ongoing = tours.filter((t: any) => t.isOngoing);
    const rest = tours.filter((t: any) => !t.isOngoing);
    // Light randomization within qualified results
    const rotated = [...sessionRotate(ongoing.slice(0, 18)), ...sessionRotate(rest.slice(0, 12))];
    return rotated.slice(0, 12);
  }, [tours]);

  if (isLoading) {
    return (
      <section>
        <SectionHeader />
        <div className="flex gap-4 overflow-hidden -mx-4 px-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-[4/5] w-[78%] sm:w-[60%] md:w-[44%] rounded-2xl bg-muted animate-pulse shrink-0" />
          ))}
        </div>
      </section>
    );
  }

  // Resolving empty must not delete the whole section: that pulls everything
  // below it upward. Keep the heading and settle into a compact empty line.
  if (!pool.length) {
    return (
      <section>
        <SectionHeader />
        <p className="text-sm text-muted-foreground leading-relaxed py-3">
          No featured tours right now. New tours land here as they're announced.
        </p>
      </section>
    );
  }

  return (
    <section>
      <SectionHeader />
      <Carousel className="w-full" opts={{ align: 'start', dragFree: true }}>
        <CarouselContent className="-ml-4">
          {pool.map((tour: any, idx: number) => {
            const image = tour.image || tour.artistImage || '';
            const isLCP = idx === 0;
            const date = tour.nearestShowDate
              ? format(new Date(tour.nearestShowDate), 'MMM d')
              : null;

            return (
              <CarouselItem key={tour.id} className="pl-4 basis-[78%] sm:basis-[60%] md:basis-[44%]">
                <div className="relative group">
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-muted ring-1 ring-border/30 shadow-[0_18px_42px_-22px_hsl(var(--foreground)/0.45)]">
                    {image ? (
                      <ImageWithSkeleton
                        containerClassName="absolute inset-0"
                        src={image}
                        alt={tour.artistName || tour.name}
                        width={640}
                        height={800}
                        imgClassName="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                        eagerLoad={isLCP}
                        fetchPriority={isLCP ? 'high' : 'auto'}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5">
                        <Music2 className="h-10 w-10 text-primary/40" />
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

                    {/* Stretched link → tour page (covers card; sits below the artist link) */}
                    <Link
                      to={getTourLink(tour.id, tour.name)}
                      aria-label={`${tour.name} — tour details`}
                      className="absolute inset-0 z-10"
                    />

                    {tour.isOngoing && (
                      <div className="absolute top-3 left-3 z-20 pointer-events-none">
                        <span className="text-[9px] uppercase tracking-[0.22em] text-white/90 backdrop-blur-md bg-black/30 px-2 py-1 rounded-full">
                          On tour now
                        </span>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 z-20 pointer-events-none">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-white/75 font-medium mb-1.5">
                        Featured Tour
                      </p>
                      {tour.artistId || tour.artistName ? (
                        <Link
                          to={artistUrl({ id: tour.artistId, name: tour.artistName || tour.name })}
                          onClick={(e) => e.stopPropagation()}
                          className="pointer-events-auto inline-block max-w-full"
                        >
                          <h3 className="text-white font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight truncate hover:text-primary/90 transition-colors">
                            {toTitleCase(tour.artistName || tour.name)}
                          </h3>
                        </Link>
                      ) : (
                        <h3 className="text-white font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight truncate">
                          {toTitleCase(tour.artistName || tour.name)}
                        </h3>
                      )}
                      {tour.artistName && tour.name && (
                        <p className="text-white/80 text-[13px] mt-0.5 truncate">
                          {toTitleCase(tour.name)}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2.5 text-white/75 text-[11px]">
                        {date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Next · {date}
                          </span>
                        )}
                        {tour.upcomingConcerts > 0 && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {tour.upcomingConcerts} dates
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </section>
  );
};

export default FeaturedToursCarousel;
